import { SegmentType, } from "./wsi4_interfaces";
import { isArcSegment, } from "./wsi4_typeguards";
export const dTol = 1e-5;
export function lineSegment(from, to) {
    return {
        type: SegmentType.line,
        content: {
            from: {
                entries: from,
            },
            to: {
                entries: to,
            },
        },
    };
}
export function arcSegment(from, to, center, ccw) {
    return {
        type: SegmentType.arc,
        content: {
            from: {
                entries: from,
            },
            to: {
                entries: to,
            },
            center: {
                entries: center,
            },
            ccw: ccw,
        },
    };
}
export function squareDistance(first, second) {
    const xDiff = (second.entries[0] - first.entries[0]);
    const yDiff = (second.entries[1] - first.entries[1]);
    return xDiff * xDiff + yDiff * yDiff;
}
export function vector(first, second) {
    return {
        entries: [
            second.entries[0] - first.entries[0],
            second.entries[1] - first.entries[1],
        ],
    };
}
export function add(first, vec) {
    return {
        entries: [
            first.entries[0] + vec.entries[0],
            first.entries[1] + vec.entries[1],
        ],
    };
}
export function angleOfPoint(point, center) {
    return Math.atan2(point.entries[1] - center.entries[1], point.entries[0] - center.entries[0]);
}
export function rotate(point, center, angle) {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return {
        entries: [
            center.entries[0] + (point.entries[0] - center.entries[0]) * cos - (point.entries[1] - center.entries[1]) * sin,
            center.entries[1] + (point.entries[0] - center.entries[0]) * sin + (point.entries[1] - center.entries[1]) * cos,
        ],
    };
}
export function rotateCcw(vector) {
    return {
        entries: [
            -vector.entries[1],
            vector.entries[0],
        ],
    };
}
export function rotateCw(vector) {
    return {
        entries: [
            vector.entries[1],
            -vector.entries[0],
        ],
    };
}
export function mult(n, vector) {
    return {
        entries: [
            n * vector.entries[0],
            n * vector.entries[1],
        ],
    };
}
export function squareNorm(p) {
    return p.entries[0] * p.entries[0] + p.entries[1] * p.entries[1];
}
export function normalize(p) {
    return mult(1. / Math.sqrt(squareNorm(p)), p);
}
export function centerOfArc(startPoint, vector, ccw, radius) {
    const normal = normalize(ccw ? rotateCcw(vector) : rotateCw(vector));
    return add(startPoint, mult(radius, normal));
}
export function radius(o) {
    return Math.sqrt(squareDistance(o.center, o.from));
}
export function angle(o) {
    let angle = angleOfPoint(o.to, o.center) - angleOfPoint(o.from, o.center);
    if (o.ccw && angle < 0.) {
        angle += 2. * Math.PI;
    }
    if (!o.ccw && angle > 0.) {
        angle -= 2. * Math.PI;
    }
    return angle;
}
export function beginTangent(segment) {
    const vec = vector(segment.center, segment.from);
    return normalize(segment.ccw ? rotateCcw(vec) : rotateCw(vec));
}
export function endTangent(segment) {
    const vec = vector(segment.center, segment.to);
    return normalize(segment.ccw ? rotateCcw(vec) : rotateCw(vec));
}
export function lineSegmentContent(from, to) {
    return {
        from: from,
        to: to,
    };
}
export function arcSegmentContent(from, to, center, ccw) {
    return {
        from: from,
        to: to,
        center: center,
        ccw: ccw,
    };
}
export function closePoints(f, s) {
    return squareDistance(f, s) < dTol;
}
export function reverseLineSegment(lineSegment) {
    return {
        from: lineSegment.to,
        to: lineSegment.from,
    };
}
export function reverseArcSegment(arcSegment) {
    return {
        from: arcSegment.to,
        to: arcSegment.from,
        center: arcSegment.center,
        ccw: !arcSegment.ccw,
    };
}
export function isClosed(p) {
    return closePoints(p[0].content.from, p[p.length - 1].content.to);
}
export function createCircle(radius) {
    return [
        arcSegment([
            radius,
            0.,
        ], [
            radius,
            0.,
        ], [
            0.,
            0.,
        ], true),
    ];
}
export function createRectangle(width, height, cornerRadius) {
    const diameter = 2. * cornerRadius;
    if (diameter > Math.min(width, height)) {
        throw Error("Corners too big.");
    }
    const addWidthLineSegment = width > diameter;
    const addHeightLineSegment = height > diameter;
    const addCornerArcs = cornerRadius !== 0.;
    const path = [];
    if (addWidthLineSegment) {
        path.push(lineSegment([
            cornerRadius,
            0,
        ], [
            width - cornerRadius,
            0,
        ]));
    }
    if (addCornerArcs) {
        path.push(arcSegment([
            width - cornerRadius,
            0,
        ], [
            width,
            cornerRadius,
        ], [
            width - cornerRadius,
            cornerRadius,
        ], true));
    }
    if (addHeightLineSegment) {
        path.push(lineSegment([
            width,
            cornerRadius,
        ], [
            width,
            height - cornerRadius,
        ]));
    }
    if (addCornerArcs) {
        path.push(arcSegment([
            width,
            height - cornerRadius,
        ], [
            width - cornerRadius,
            height,
        ], [
            width - cornerRadius,
            height - cornerRadius,
        ], true));
    }
    if (addWidthLineSegment) {
        path.push(lineSegment([
            width - cornerRadius,
            height,
        ], [
            cornerRadius,
            height,
        ]));
    }
    if (addCornerArcs) {
        path.push(arcSegment([
            cornerRadius,
            height,
        ], [
            0,
            height - cornerRadius,
        ], [
            cornerRadius,
            height - cornerRadius,
        ], true));
    }
    if (addHeightLineSegment) {
        path.push(lineSegment([
            0,
            height - cornerRadius,
        ], [
            0,
            cornerRadius,
        ]));
    }
    if (addCornerArcs) {
        path.push(arcSegment([
            0,
            cornerRadius,
        ], [
            cornerRadius,
            0,
        ], [
            cornerRadius,
            cornerRadius,
        ], true));
    }
    if (!isClosed(path)) {
        throw Error("Path not closed.");
    }
    return path;
}
export function isCircle(p) {
    if (!isClosed(p)) {
        return false;
    }
    if (p.some(s => s.type === SegmentType.line)) {
        return false;
    }
    const firstArc = p[0].content;
    const orientation = firstArc.ccw;
    const r = radius(firstArc);
    const c = firstArc.center;
    return !(p.some(s => {
        const arc = s.content;
        return arc.ccw !== orientation || !closePoints(arc.center, c) || radius(arc) !== r;
    }));
}
export function boundingBox(path) {
    const bigM = 1e10;
    const lower = {
        entries: [
            bigM,
            bigM,
        ],
    };
    const upper = {
        entries: [
            -bigM,
            -bigM,
        ],
    };
    for (const p of path) {
        let minX = Math.min(p.content.from.entries[0], p.content.to.entries[0]);
        let minY = Math.min(p.content.from.entries[1], p.content.to.entries[1]);
        let maxX = Math.max(p.content.from.entries[0], p.content.to.entries[0]);
        let maxY = Math.max(p.content.from.entries[1], p.content.to.entries[1]);
        if (p.type === SegmentType.arc) {
            if (!isArcSegment(p.content)) {
                throw Error("Wrong type.");
            }
            const r = radius(p.content);
            maxX = Math.max(maxX, p.content.center.entries[0] + r);
            maxY = Math.max(maxY, p.content.center.entries[1] + r);
            minX = Math.min(minX, p.content.center.entries[0] - r);
            minY = Math.min(minY, p.content.center.entries[1] - r);
        }
        lower.entries[0] = Math.min(lower.entries[0], minX);
        lower.entries[1] = Math.min(lower.entries[1], minY);
        upper.entries[0] = Math.max(upper.entries[0], maxX);
        upper.entries[1] = Math.max(upper.entries[1], maxY);
    }
    return {
        lower: lower,
        upper: upper,
    };
}
function dimX(b) {
    return b.upper.entries[0] - b.lower.entries[0];
}
function dimY(b) {
    return b.upper.entries[1] - b.lower.entries[1];
}
export function dimensions(path) {
    const b = boundingBox(path);
    return [
        dimX(b),
        dimY(b),
    ];
}
export function centerOfPath(p) {
    const b = boundingBox(p);
    return add(b.lower, {
        entries: [
            dimX(b) * 0.5,
            dimY(b) * 0.5,
        ],
    });
}
export function translateLineSegment(point, l) {
    return {
        from: add(point, l.from),
        to: add(point, l.to),
    };
}
export function translateArcSegment(point, a) {
    return {
        from: add(point, a.from),
        to: add(point, a.to),
        center: add(point, a.center),
        ccw: a.ccw,
    };
}
export function translate(point, p) {
    return p.map(s => {
        if (s.type === SegmentType.line) {
            return {
                type: s.type,
                content: translateLineSegment(point, s.content),
            };
        }
        else {
            if (!isArcSegment(s.content)) {
                throw Error("Wrong type.");
            }
            return {
                type: s.type,
                content: translateArcSegment(point, s.content),
            };
        }
    });
}
export function intersects(first, second) {
    const u = vector(first.from, first.to);
    const v = vector(second.from, second.to);
    if (Math.abs(u.entries[1]) < dTol) {
        if (Math.abs(v.entries[1]) < dTol) {
            return false;
        }
        else {
            return intersects(second, first);
        }
    }
    const det = u.entries[0] * v.entries[1] - u.entries[1] * v.entries[0];
    if (Math.abs(det) < dTol) {
        return false;
    }
    const detDet = det * det;
    const lambdaDet = u.entries[0] * first.from.entries[1] - u.entries[1] * first.from.entries[0] + u.entries[1] * second.from.entries[0] - u.entries[0] * second.from.entries[1];
    if ((lambdaDet * det) < -dTol || (lambdaDet * det) > (1. + dTol) * detDet) {
        return false;
    }
    const u1u1 = u.entries[1] * u.entries[1];
    return ((lambdaDet * det * u.entries[1] * v.entries[1]) >= (first.from.entries[1] * u.entries[1] * detDet - second.from.entries[1] * u.entries[1] * detDet - dTol * u1u1 * detDet))
        && ((lambdaDet * det * u.entries[1] * v.entries[1]) <= ((1 + dTol) * u1u1 * detDet + first.from.entries[1] * u.entries[1] * detDet - second.from.entries[1] * u.entries[1] * detDet));
}
export function toRadians(angle) {
    return (angle * 2. * Math.PI) / 360.;
}
export function toDegree(angle) {
    return (angle * 360.) / (2. * Math.PI);
}
export function createHoleCutout(holeDimX, holeDimY, radius) {
    return [
        arcSegment([
            holeDimX,
            holeDimY - radius,
        ], [
            holeDimX,
            holeDimY - radius,
        ], [
            holeDimX,
            holeDimY,
        ], false),
    ];
}
export function intersection(first, second) {
    if (!intersects(first, second)) {
        return undefined;
    }
    const u = vector(first.from, first.to);
    const v = vector(second.from, second.to);
    const det = u.entries[0] * v.entries[1] - u.entries[1] * v.entries[0];
    if (Math.abs(det) < dTol) {
        return undefined;
    }
    const lambda = (u.entries[0] * first.from.entries[1] - u.entries[1] * first.from.entries[0] + u.entries[1] * second.from.entries[0] - u.entries[0] * second.from.entries[1]) / det;
    return add(second.from, mult(lambda, v));
}
export function scalarProduct(first, second) {
    return first.entries[0] * second.entries[0] + first.entries[1] * second.entries[1];
}
